import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { pastLastWhistle } from "../../../../engine/autopilot";
import { draftRound, keptOff } from "../../../../engine/scheduler";
import { availablePlayers, courtsReady, currentRound, heldForScores, history, owedScores, playersIn, startsByItselfAt } from "../../../../engine/selectors";
import { MINUTE, PAUSE_LABEL_MAX } from "../../../../engine/state";
import type { Lineup, Pair, PlayerId, Round, TournamentState } from "../../../../engine/types";
import { gamesDone, windDownNow } from "../../../../engine/winddown";
import { breakNow, countdown, hhmm, resumeLine, waitingFor } from "../../../../shared/day";
import { ConfirmButton, CourtBadge, Toggle, betweenRounds, clockTime, eventName, mmss, nameOf, phaseLabel, remaining, scoreText, useOpened, useTick, type TabProps } from "./common";
import { Fold } from "./Fold";
import { SlidesCard, slidesCardShown } from "./SlidesCard";
import { WindDownCard, doneLine, owedWords } from "./WindDown";
import { whistleAt, whistleWhen } from "./whistle";

/** Who is in a swap: the round it is in and the player going out. */
interface Swap {
  roundNo: number;
  out: PlayerId;
}

/** Which round a swap is in: the preview, the one being played, or one that is over. */
type SwapWhen = "preview" | "playing" | "over";

/** The line-up on show: the round on court, the next one, or the last one played while a court of it has no score. */
type View = "court" | "next" | "fix";
const VIEWS: View[] = ["court", "fix", "next"];

/** A court as the swap sees it: who is on it, and whether its score is in (then it cannot change). */
interface SwapCourt {
  court: number;
  a: Pair;
  b: Pair;
  eventId: string | null;
  /** What to show where the score goes, or null on the preview. */
  score: string | null;
  /** The round is over and this court has no score. */
  owed: boolean;
  locked: boolean;
}

/** The round each player last played in, 0 for never, looking only at rounds before `beforeNo`. */
function lastPlayed(state: TournamentState, beforeNo: number): Record<PlayerId, number> {
  const last: Record<PlayerId, number> = {};
  for (const round of state.rounds) {
    if (round.no >= beforeNo) break;
    for (const id of playersIn(round.lineup)) last[id] = round.no;
  }
  return last;
}

/**
 * "Round 7: swap Luke out for": whoever is not on court in it, fewest games first, then the
 * longest wait, then (not once the round is over) the players on other courts.
 */
function SwapChooser({ t, state, swap, courts, when, onDone }: Pick<TabProps, "t" | "state"> & { swap: Swap; courts: SwapCourt[]; when: SwapWhen; onDone: () => void }) {
  const playing = new Set(courts.flatMap((c) => [...c.a, ...c.b]));
  const last = lastPlayed(state, swap.roundNo);
  const games = history(state).gamesPlayed;
  const over = when === "over";
  // once a round is over, whoever really played may have gone on a break or home since
  const resting = Object.values(state.players)
    .filter((p) => (over ? p.status !== "expected" : p.status === "in") && !playing.has(p.id))
    .sort((x, y) => (games[x.id] ?? 0) - (games[y.id] ?? 0) || (last[x.id] ?? 0) - (last[y.id] ?? 0) || nameOf(state, x.id).localeCompare(nameOf(state, y.id)));
  const others = over ? [] : courts.filter((c) => !c.locked && !c.a.includes(swap.out) && !c.b.includes(swap.out));
  const who = nameOf(state, swap.out);
  const go = (id: PlayerId) => {
    t.send({ type: "lineup.swapped", roundNo: swap.roundNo, out: swap.out, in: id });
    onDone();
  };
  return (
    <div className="ad-swap">
      <h3>Round {swap.roundNo}{when === "preview" ? ", the preview" : over ? ", already played" : ""}: swap {who} out for</h3>
      {resting.length ? (
        <div className="ad-names">
          {resting.map((p) => {
            const n = games[p.id] ?? 0;
            return (
              <button key={p.id} type="button" className="ad-name" onClick={() => go(p.id)}>
                <span>{nameOf(state, p.id)}</span>
                <span className="ad-name-games">{n} {n === 1 ? "game" : "games"}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="ad-note">{over ? "There is nobody else to pick." : "Nobody is resting."}</p>
      )}
      {others.length > 0 && (
        <>
          <h3>Or {who} changes places with</h3>
          {others.map((c) => (
            <div key={c.court} className="ad-swap-court">
              <CourtBadge court={c.court} />
              <div className="ad-names">
                {[...c.a, ...c.b].map((id) => (
                  <button key={id} type="button" className="ad-name" onClick={() => go(id)}>{nameOf(state, id)}</button>
                ))}
              </div>
            </div>
          ))}
        </>
      )}
      <button type="button" className="btn quiet ad-wide" onClick={onDone}>Leave {who} where they are</button>
    </div>
  );
}

/**
 * A round's courts, a short row each: the court, its mini game and its score on one line, then
 * the two pairs as two rows of names. Every name is a key: tap one to swap that player, and the
 * chooser opens under that court.
 */
function SwapCourts({ t, state, roundNo, courts, when, swap, setSwap }: Pick<TabProps, "t" | "state"> & {
  roundNo: number;
  courts: SwapCourt[];
  when: SwapWhen;
  swap: Swap | null;
  setSwap: (swap: Swap | null) => void;
}) {
  const pair = (c: SwapCourt, ids: Pair) => (
    <div className="ad-names">
      {ids.map((id) => {
        const on = swap?.roundNo === roundNo && swap.out === id;
        return c.locked
          ? <span key={id} className="ad-name still">{nameOf(state, id)}</span>
          : (
            <button key={id} type="button" className={"ad-name" + (on ? " on" : "")} aria-pressed={on}
              onClick={() => setSwap(on ? null : { roundNo, out: id })}>
              {nameOf(state, id)}
            </button>
          );
      })}
    </div>
  );
  return (
    <ul className="ad-list ad-lineups">
      {courts.map((c) => {
        const open = swap?.roundNo === roundNo && (c.a.includes(swap.out) || c.b.includes(swap.out));
        return (
          <li key={c.court} className={"ad-lineup" + (c.locked ? " locked" : "")}>
            <div className="ad-lineup-head">
              <CourtBadge court={c.court} />
              <b>Court {c.court}</b>
              {c.eventId && <span className="ad-event">{eventName(state, c.eventId)}</span>}
              {c.score && <span className={"ad-score" + (c.owed ? " ad-owed" : "")}>{c.score}</span>}
            </div>
            <div className="ad-pairs">
              {pair(c, c.a)}
              <span className="ad-v" aria-hidden="true">v</span>
              {pair(c, c.b)}
            </div>
            {open && swap && <SwapChooser t={t} state={state} swap={swap} courts={courts} when={when} onDone={() => setSwap(null)} />}
          </li>
        );
      })}
    </ul>
  );
}

function Resting({ state, lineup }: { state: TournamentState; lineup: Lineup }) {
  // the preview is always the next round's
  const roundNo = state.rounds.length + 1;
  const off = keptOff(state, roundNo).filter((id) => lineup.sittingOut.includes(id));
  return (
    <>
      <p className="ad-note">
        <b>Sitting out:</b> {lineup.sittingOut.length ? lineup.sittingOut.map((id) => nameOf(state, id)).join(", ") : "nobody"}
      </p>
      {off.length > 0 && (
        <p className="ad-note">
          {off.map((id) => nameOf(state, id)).join(", ")} sits out round {roundNo} by the day's rule, to keep an eye on the tech
        </p>
      )}
      {lineup.catchUp.length > 0 && (
        <p className="ad-note">
          <b>Catch-up game:</b> {lineup.catchUp.map((id) => nameOf(state, id)).join(", ")}
        </p>
      )}
    </>
  );
}

const COURT_CHOICES = [4, 3, 2];
const LUNCH_CHOICES = [20, 30, 45];
const LUNCH_NAME = "Lunch";
const LUNCH_STEP = 5 * MINUTE;
/** Finish now asks first while more than this is left and a court has not scored. */
const FINISH_ASKS_OVER = 30_000;
/** How long after this admin's own tap a change from the server is taken as theirs. */
const OWN_MS = 5000;
/** A draw with nobody on court: only a wind down whose sum can no longer be met gives one. */
const NOBODY_TO_DRAW = "Nobody can be drawn to play. Tap Cancel wind down to carry on as usual.";

function whistleInput(ms: number | null): string {
  return ms === null ? "" : clockTime(ms);
}

/** "Court 2 has no score yet." or "Courts 2 and 4 have no score yet." */
function notReadyWords(courts: number[]): string {
  if (courts.length === 1) return `Court ${courts[0]} has no score yet.`;
  return `Courts ${courts.slice(0, -1).join(", ")} and ${courts[courts.length - 1]} have no score yet.`;
}

/** A started round's courts as the swap sees them. A score is only owed once the round is over. */
function swapCourts(round: Round, playing: boolean): SwapCourt[] {
  return round.games.map((g) => {
    const open = !g.voided && g.score === null;
    return {
      court: g.court, a: g.a, b: g.b, eventId: g.eventId,
      score: g.voided ? "void" : g.score ? scoreText(g.score) : playing ? "playing" : "owed",
      owed: open && !playing,
      locked: !open,
    };
  });
}

/**
 * A break with a time to be back. Before it starts: how long and what to call it, kept short so
 * Start it is on the first screen of every phone. While it is on: when people are due back, and
 * five minutes more or less.
 */
function Lunch({ t, state, onClose }: Pick<TabProps, "t" | "state"> & { onClose: () => void }) {
  const [minutes, setMinutes] = useState(30);
  const [words, setWords] = useState(LUNCH_NAME);
  const now = t.now();
  const on = breakNow(state, now);
  const until = state.pausedUntil;
  const form = useOpened<HTMLDivElement>(!(on && until !== null));

  if (on && until !== null) {
    // five more counts from now once the time has passed, so the new time is never already gone
    const more = Math.max(until, now) + LUNCH_STEP;
    const less = until - LUNCH_STEP;
    return (
      <div className="ad-lunch" role="status">
        <b>{on.label}</b>
        <span>{on.over ? "The time has passed. Every screen says: Back on court now." : `${on.backAt}, ${countdown(on.leftMs ?? 0)} to go.`}</span>
        <div className="ad-grid2">
          <button type="button" className="btn" onClick={() => t.send({ type: "pause.all", on: true, until: more })}>5 min more</button>
          <button type="button" className="btn" disabled={less <= now} onClick={() => t.send({ type: "pause.all", on: true, until: less })}>5 min less</button>
        </div>
      </div>
    );
  }

  const label = words.trim() || LUNCH_NAME;
  return (
    <div className="ad-lunch ad-lunch-form" ref={form}>
      <div className="ad-seg" role="group" aria-label="Minutes">
        {LUNCH_CHOICES.map((n) => (
          <button key={n} type="button" className={"ad-chip" + (minutes === n ? " on" : "")} aria-pressed={minutes === n}
            aria-label={`${n} minutes`} onClick={() => setMinutes(n)}>
            {n} min
          </button>
        ))}
      </div>
      <label className="ad-field">
        <span className="ad-field-top"><span>What to call it</span><span className="ad-counter">{words.length} of {PAUSE_LABEL_MAX}</span></span>
        <input value={words} maxLength={PAUSE_LABEL_MAX} placeholder={LUNCH_NAME} autoComplete="off" onChange={(e) => setWords(e.target.value)} />
      </label>
      <div className="ad-grid2">
        <button type="button" className="btn accent"
          onClick={() => { t.send({ type: "pause.all", on: true, until: now + minutes * MINUTE, label }); onClose(); }}>
          Start it
        </button>
        <button type="button" className="btn quiet" onClick={onClose}>Cancel</button>
      </div>
      <p className="ad-note">Everything pauses. Every screen says {label}, back at {hhmm(now + minutes * MINUTE)}, and counts down. Nothing starts by itself after it.</p>
    </div>
  );
}

export function RoundsTab({ t, state, say }: TabProps) {
  useTick();
  const now = t.now();
  const [attempt, setAttempt] = useState(0);
  // only what this admin has typed: an untouched box follows the day, so a phone that has been
  // asleep cannot put an old time back
  const [whistleEdit, setWhistleEdit] = useState<string | null>(null);
  const [swap, setSwap] = useState<Swap | null>(null);
  const [lunch, setLunch] = useState(false);
  // the line-up this admin asked to see, and the part of the day they asked in
  const [picked, setPicked] = useState<{ view: View; phase: string } | null>(null);
  const left = remaining(state, now);
  const round = currentRound(state);
  const live = round && state.clock.roundNo === round.no ? round : null;
  const owed = owedScores(state);
  const late = pastLastWhistle(state, now);
  const playing = state.clock.phase === "playing";
  // no game on: idle, or a changeover that only counts down to an admin's Start (decision 41)
  const between = betweenRounds(state);
  const nextNo = state.rounds.length + 1;
  const courts = state.config.courts;
  // only the round being played, and only until its first score, can be taken back
  const canTakeBack = playing && live !== null && live.no === state.rounds.length && !live.games.some((g) => g.score);
  const onCourt = playing ? live : null;
  // the newest round that is over: a wrong name on a court with no score can still be put right
  const done = state.rounds[state.rounds.length - (onCourt ? 2 : 1)] ?? null;
  const fixable = done && done.games.some((g) => !g.voided && !g.score) ? done : null;
  const waiting = waitingFor(state, now);
  const lastWhistle = state.config.lastWhistleAt;
  const whistleLive = whistleInput(lastWhistle);
  const whistle = whistleEdit ?? whistleLive;
  const finishAsks = onCourt !== null && left !== null && left > FINISH_ASKS_OVER && onCourt.games.some((g) => !g.voided && !g.score);

  // Once tapped, the line-up on show stays put until the next whistle. Then it follows the day
  // again: the round on court while one is played, the next round between rounds. A round that
  // was on show when its whistle went stays on show, as "already played", while a court of it has
  // no score. The heading always names the round, so a change at a whistle is read, not guessed.
  const phase = `${state.clock.phase}:${state.rounds.length}`;
  const can: Record<View, boolean> = { court: onCourt !== null, next: true, fix: fixable !== null };
  const lineups: Record<View, { roundNo: number; courts: SwapCourt[] }> = {
    court: { roundNo: onCourt?.no ?? 0, courts: onCourt ? swapCourts(onCourt, true) : [] },
    fix: { roundNo: fixable?.no ?? 0, courts: fixable ? swapCourts(fixable, false) : [] },
    next: { roundNo: nextNo, courts: state.draft ? state.draft.courts.map((c) => ({ ...c, score: null, owed: false, locked: false })) : [] },
  };
  // A swap list that is open holds the view on its round, whatever the clock does, until it is
  // closed. When that player can no longer be swapped in that round, the list closes and says so.
  const held: View | null = swap
    ? VIEWS.find((v) => lineups[v].roundNo === swap.roundNo && lineups[v].courts.some((c) => !c.locked && (c.a.includes(swap.out) || c.b.includes(swap.out)))) ?? null
    : null;
  const lost = swap !== null && held === null;
  const lostWords = !swap
    ? ""
    : swap.roundNo === nextNo
      ? `The preview for round ${swap.roundNo} was drawn again. The swap list has closed.`
      : state.rounds.slice(-2).some((r) => r.no === swap.roundNo && r.games.some((g) => (g.voided || g.score) && (g.a.includes(swap.out) || g.b.includes(swap.out))))
        ? `${nameOf(state, swap.out)}'s court in round ${swap.roundNo} has its score. The swap list has closed.`
        : `Round ${swap.roundNo} can no longer be swapped. The swap list has closed.`;
  useEffect(() => {
    if (!lost) return;
    setSwap(null);
    say(lostWords);
  }, [lost]);
  const openSwap = lost ? null : swap;
  const shown = useRef<{ view: View; roundNo: number } | null>(null);
  const fell = !onCourt && fixable !== null && shown.current !== null && shown.current.view !== "next" && shown.current.roundNo === fixable.no;
  const view: View = held ?? (picked && picked.phase === phase && can[picked.view] ? picked.view : onCourt ? "court" : fell ? "fix" : "next");
  useEffect(() => {
    shown.current = { view, roundNo: lineups[view].roundNo };
  });
  // what this admin does by hand moves the page as any tap does; only the day's own changes are held still
  const byHand = useRef(false);
  // when this admin's own Start, Finish now, pause or take back comes back from the server: that
  // change is theirs, not the day's, so the clock comes into view instead of the line-ups holding
  const handAt = useRef(0);
  const mine = () => {
    handAt.current = Date.now();
  };
  const show = (next: View) => {
    byHand.current = true;
    setSwap(null);
    setPicked({ view: next, phase });
  };
  const pickSwap = (next: Swap | null) => {
    byHand.current = true;
    setSwap(next);
  };

  // The page does not jump when the day changes what is on show (a whistle, a late score, a list
  // the day closed). Scrolled down, or with a swap list open, the line-ups stay where they are on
  // the glass and the page moves behind them. If the page got too short for that, it is held
  // long enough (the floor) until the admin scrolls back up.
  const tabRoot = useRef<HTMLDivElement | null>(null);
  const lineupCard = useRef<HTMLElement | null>(null);
  const stood = useRef<{ key: string; list: boolean; top: number; scrollY: number; floor: boolean } | null>(null);
  // a court that gets its score closes up, so the count of those is part of what the day changed
  const standKey = `${phase}|${waiting?.why ?? ""}|${state.pausedAll}|${canTakeBack}|${view}|${openSwap !== null}|${lineups[view].courts.filter((c) => c.locked).length}|${courtsReady(state)?.waiting.length ?? ""}|${Boolean(state.windDown)}`;
  useLayoutEffect(() => {
    const root = tabRoot.current;
    const card = lineupCard.current;
    if (!root || !card) return;
    // an open swap list is what stays still; with none open, the top of the line-ups
    const list = card.querySelector(".ad-swap");
    const at = list ?? card;
    const was = stood.current;
    if (was !== null && was.key !== standKey && Date.now() - handAt.current < OWN_MS) {
      handAt.current = 0;
      byHand.current = false;
      root.style.minHeight = "";
      const clock = root.querySelector(".ad-clock");
      if (clock && clock.getBoundingClientRect().top < 0) window.scrollTo(0, 0);
      stood.current = { key: standKey, list: list !== null, top: at.getBoundingClientRect().top + window.scrollY, scrollY: window.scrollY, floor: false };
      return;
    }
    const moved = was !== null && was.key !== standKey && !byHand.current;
    byHand.current = false;
    if (!was || !(moved || was.floor)) {
      stood.current = { key: standKey, list: list !== null, top: at.getBoundingClientRect().top + window.scrollY, scrollY: window.scrollY, floor: false };
      return;
    }
    // Where the page was before this draw. The browser's own figure is right unless the page has
    // just got too short and been pulled up to its end: only then go by the last place seen.
    const nowY = window.scrollY;
    const atEnd = nowY + window.innerHeight >= document.documentElement.scrollHeight - 1;
    const wasY = atEnd ? Math.max(nowY, was.scrollY) : nowY;
    root.style.minHeight = "";
    const top = at.getBoundingClientRect().top + window.scrollY;
    const hold = moved && was.list === (list !== null) && (wasY > 0 || list !== null);
    const wantY = Math.max(0, wasY + (hold ? top - was.top : 0));
    const short = wantY + window.innerHeight - document.documentElement.scrollHeight;
    if (short > 0) root.style.minHeight = `${root.getBoundingClientRect().height + short}px`;
    if (Math.abs(window.scrollY - wantY) > 0.5) window.scrollTo(0, wantY);
    stood.current = { key: standKey, list: list !== null, top, scrollY: wantY, floor: short > 0 };
  });
  // where the page is, kept as the admin scrolls, so a draw knows where it was before it
  useEffect(() => {
    const scrolled = () => {
      if (stood.current) stood.current.scrollY = window.scrollY;
    };
    window.addEventListener("scroll", scrolled, { passive: true });
    return () => window.removeEventListener("scroll", scrolled);
  }, []);

  // the round being played is counted, not owed: its scores are not due until the whistle
  const scoredNow = onCourt ? onCourt.games.filter((g) => g.voided || g.score).length : 0;
  const owedBefore = onCourt ? owed.filter((o) => o.roundNo !== onCourt.no) : owed;

  // decision 41: between rounds, how many courts of the round just played have their score in
  const ready = courtsReady(state);
  const allDone = gamesDone(state) && !playing;
  const latest = state.rounds[state.rounds.length - 1];
  const startBlock = playing
    ? "A round is being played."
    : state.pausedAll
      ? "Everything is paused."
      : allDone
        ? "The games are done."
        : !state.draft
          ? availablePlayers(state).length < state.config.playersPerCourt
            ? "Not enough people here to draw a round."
            : state.mode === "mexicano" && latest?.games.some((g) => !g.voided && !g.score)
              ? "No preview yet: it is drawn when every score is in."
              : "No preview yet."
          : !state.draft.courts.length
            ? NOBODY_TO_DRAW
            : null;
  // a court not ready, or the last whistle, does not stop an admin: Start asks first
  const notReady = startBlock === null && ready ? ready.waiting : [];
  const lateAsks = startBlock === null && late && lastWhistle !== null;
  const startAsks = notReady.length > 0 || lateAsks;
  const startQuestion = [
    notReady.length ? notReadyWords(notReady) : "",
    lateAsks ? `This game would end after the last whistle (${whistleLive}).` : "",
    `Start round ${nextNo} anyway?`,
  ].filter(Boolean).join(" ");
  const start = () => {
    if (!state.draft || !state.draft.courts.length) return;
    mine();
    t.send({ type: "round.started", lineup: state.draft });
  };
  const windPlan = allDone ? windDownNow(state) : null;

  // Switching "Start rounds by themselves" on starts the next round at once when one is ready (the
  // server's autopilot, decision 41), so then it asks first (his word, 16 Oct). Mid-round, or when
  // nothing would start yet, it is switched at once as before.
  const [askAuto, setAskAuto] = useState(false);
  const autoStartsNow = !state.config.autoAdvance && state.clock.phase === "idle" && !state.pausedAll && state.rounds.length > 0
    && state.draft !== null && state.draft.courts.length > 0 && !heldForScores(state) && (startsByItselfAt(state) ?? 0) <= now && !late;
  const autoBox = useOpened<HTMLDivElement>(askAuto && autoStartsNow);
  useEffect(() => {
    if (!autoStartsNow) setAskAuto(false);
  }, [autoStartsNow]);
  const setAuto = (on: boolean) => {
    if (on && autoStartsNow) {
      setAskAuto(true);
      return;
    }
    setAskAuto(false);
    t.send({ type: "config.updated", patch: { autoAdvance: on } });
  };

  function regenerate() {
    if (availablePlayers(state).length < state.config.playersPerCourt) {
      say("Not enough players here to fill a court.");
      return;
    }
    const n = attempt + 1;
    const lineup = draftRound(state, { attempt: n });
    // a wind down whose sum can no longer be met draws nobody: never send an empty line-up
    if (!lineup.courts.length) {
      say(NOBODY_TO_DRAW);
      return;
    }
    setAttempt(n);
    t.send({ type: "round.drafted", lineup });
  }

  function setLastWhistle() {
    setWhistleEdit(null);
    if (whistle === whistleLive) return;
    if (!whistle) {
      t.send({ type: "config.updated", patch: { lastWhistleAt: null } });
      return;
    }
    // the whistle keeps its day: a time typed before Sunday stays on Sunday
    t.send({ type: "config.updated", patch: { lastWhistleAt: whistleAt(whistle, lastWhistle, now) } });
  }

  // the slides before round 1, on the owner's phone only: first while they are on, under the clock before
  const slidesCard = slidesCardShown(t, state) ? <SlidesCard t={t} state={state} /> : null;
  const slidesOn = typeof state.slides === "number";

  const settings = `${courts} courts, ${state.config.autoAdvance ? "starts by itself" : "waits for you"}, whistle ${lastWhistle !== null ? whistleLive : "not set"}`;

  return (
    <div className="ad-tab ad-rounds" ref={tabRoot}>
      {slidesOn ? slidesCard : null}
      <section className="ad-card ad-clock">
        <div className="ad-clock-what">
          <div className="ad-clock-phase">
            {phaseLabel(state)}
            {state.clock.roundNo !== null && !between && `, round ${state.clock.roundNo}`}
          </div>
          <div className="ad-clock-sub">
            {state.pausedAll && <span className="ad-tag warn">Paused</span>}
            {state.mode === "mexicano" ? "Mexicano" : "Americano"}
            {between && `, ${state.rounds.length} ${state.rounds.length === 1 ? "round" : "rounds"} so far`}
          </div>
        </div>
        <div className="ad-clock-count">{left === null ? "--:--" : mmss(left)}</div>
      </section>

      <p className={"ad-note ad-owedline" + (owedBefore.length ? " ad-owed" : "")}>
        {onCourt ? `Round ${onCourt.no}: ${scoredNow} of ${onCourt.games.length} in.` : ""}
        {/* the round ends by itself once the last court scores (decision 12): no need to wait for the clock */}
        {onCourt && scoredNow > 0 && scoredNow < onCourt.games.length ? " It ends when the last one is in." : ""}
        {onCourt && owedBefore.length ? " " : ""}
        {owedBefore.length ? `Owed: ${owedWords(owedBefore)}` : onCourt ? "" : "No scores owed."}
      </p>

      {slidesOn ? null : slidesCard}

      <section className="ad-card ad-flat">
        {state.pausedAll ? (
          <>
            <button type="button" className="btn accent ad-wide" onClick={() => { setLunch(false); mine(); t.send({ type: "pause.all", on: false }); }}>
              {state.pausedUntil !== null ? "Back on court" : "Resume"}
            </button>
            <p className="ad-note">{resumeLine(state, now)}</p>
          </>
        ) : allDone && windPlan ? (
          <div className="ad-waiting ad-done" role="status">
            <p>{doneLine(state, windPlan, owed)}</p>
            <p>The podium and the awards are on the More tab, under Reveal.</p>
          </div>
        ) : (
          <>
            {ready && !playing && (
              <p className={"ad-ready" + (ready.waiting.length ? "" : " all")} role="status">
                <span>Courts ready: <b>{ready.ready} of {ready.of}</b></span>
                {ready.waiting.length > 0 && <span className="ad-ready-why">{notReadyWords(ready.waiting)}</span>}
              </p>
            )}
            {startAsks ? (
              <ConfirmButton label={`Start round ${nextNo}`} className="btn accent ad-wide ad-start" yes="Start it"
                question={startQuestion} onConfirm={start} />
            ) : (
              <button type="button" className="btn accent ad-wide ad-start" disabled={startBlock !== null} onClick={start}>
                Start round {nextNo}
              </button>
            )}
          </>
        )}
        {/* only while it can be done: the round being played, until its first score. It takes the
            place of the line that says why Start is off. */}
        {canTakeBack && live ? (
          <ConfirmButton label="Take this round back" className="btn quiet ad-wide" yes="Take it back"
            question={`Round ${live.no} goes back to being the preview.${state.config.autoAdvance ? " Rounds will not start by themselves until you switch that back on." : ""}`}
            onConfirm={() => { setSwap(null); mine(); t.send({ type: "round.cancelled" }); }} />
        ) : !state.pausedAll && startBlock && !allDone ? (
          <p className="ad-note">{startBlock}</p>
        ) : null}
        {!state.pausedAll && lateAsks && state.config.autoAdvance && <p className="ad-note">Past the last whistle: no more rounds start by themselves.</p>}
        {/* decision 41: the next round waits for Start. Said once, between rounds, while no court is
            still being played; with every score in, that Start can go now, however early it is */}
        {!state.config.autoAdvance && !state.pausedAll && !playing && !allDone && startBlock === null && state.rounds.length > 0 && (
          <p className="ad-note">
            {ready && !ready.waiting.length && !owed.length
              ? "Every score is in. Start as soon as the players are on court: no need to wait for the clock."
              : "The next round starts when you tap Start. Check every court is ready first."}
          </p>
        )}
        {waiting?.why === "scores" && (
          <div className="ad-waiting" role="status">
            <p>Nobody entered a score for round {waiting.roundNo}, so round {waiting.roundNo + 1} is waiting. Enter a score or tap Start.</p>
          </div>
        )}
        {waiting?.why === "soon" && (
          <div className="ad-waiting" role="status">
            <p>Round {waiting.roundNo} starts in {countdown(waiting.startsAt - now)}. A score for round {waiting.roundNo - 1} came in late. Tap Start to go now.</p>
          </div>
        )}
        {state.pausedUntil !== null && <Lunch t={t} state={state} onClose={() => setLunch(false)} />}
        {/* the four keys for a round's clock: put away between rounds, when none of them can do
            anything (with rounds waiting for Start, the changeover is between rounds too) */}
        {/* Skip changeover only with rounds starting by themselves: with them off a changeover is
            between rounds, so the key could never be used. Then Finish now takes the whole row. */}
        {!between && (
          <div className={"ad-grid2" + (state.config.autoAdvance ? "" : " ad-keys3")}>
            {finishAsks && onCourt ? (
              <ConfirmButton label="Finish now" className="btn" yes="End it"
                question={`Round ${onCourt.no} has ${mmss(left ?? 0)} left. End it now on every court?`}
                onConfirm={() => { mine(); t.send({ type: "round.finished" }); }} />
            ) : (
              <button type="button" className="btn" disabled={!playing} onClick={() => { mine(); t.send({ type: "round.finished" }); }}>
                Finish now
              </button>
            )}
            {state.config.autoAdvance && (
              <button type="button" className="btn" disabled={state.clock.phase !== "changeover"} onClick={() => { mine(); t.send({ type: "round.closed" }); }}>
                Skip changeover
              </button>
            )}
            <button type="button" className="btn" onClick={() => t.send({ type: "clock.extended", ms: MINUTE })}>
              Extend 1 min
            </button>
            <button type="button" className="btn" disabled={left === null || left < MINUTE}
              onClick={() => t.send({ type: "clock.extended", ms: -MINUTE })}>
              1 min less
            </button>
          </div>
        )}
        {/* one place for stopping the hall: the two keys, or the lunch form they open, in the same spot */}
        {state.pausedUntil !== null ? null : lunch ? (
          <Lunch t={t} state={state} onClose={() => setLunch(false)} />
        ) : state.pausedAll ? (
          <button type="button" className="btn quiet ad-wide" onClick={() => setLunch(true)}>Give this pause a time</button>
        ) : (
          <div className="ad-grid2">
            <button type="button" className="btn warn" onClick={() => { mine(); t.send({ type: "pause.all", on: true }); }}>
              Pause all
            </button>
            <button type="button" className="btn" onClick={() => setLunch(true)}>
              Lunch
            </button>
          </div>
        )}
      </section>

      <section className="ad-card" ref={lineupCard}>
        <h2>
          {view === "court" && onCourt
            ? `On court now: round ${onCourt.no}`
            : view === "fix" && fixable
              ? `Round ${fixable.no}, already played`
              : allDone
                ? "No next round"
                : `Next round: round ${nextNo}`}
        </h2>
        <div className="ad-seg" role="group" aria-label="Which line-up">
          <button type="button" className={"ad-chip" + (view === "court" ? " on" : "")} aria-pressed={view === "court"}
            disabled={!can.court} onClick={() => show("court")}>
            On court
          </button>
          <button type="button" className={"ad-chip" + (view === "next" ? " on" : "")} aria-pressed={view === "next"} onClick={() => show("next")}>
            Next round
          </button>
          {fixable && (
            <button type="button" className={"ad-chip" + (view === "fix" ? " on" : "")} aria-pressed={view === "fix"} onClick={() => show("fix")}>
              Round {fixable.no}
            </button>
          )}
        </div>
        {view === "court" && onCourt ? (
          <>
            {onCourt.games.some((g) => !g.voided && !g.score) && <p className="ad-note">Tap a name to swap that player.</p>}
            <SwapCourts t={t} state={state} roundNo={onCourt.no} when="playing" swap={openSwap} setSwap={pickSwap} courts={lineups.court.courts} />
          </>
        ) : view === "fix" && fixable ? (
          <>
            <p className="ad-note">Tap a wrong name to put it right.</p>
            <SwapCourts t={t} state={state} roundNo={fixable.no} when="over" swap={openSwap} setSwap={pickSwap} courts={lineups.fix.courts} />
            <p className="ad-note">Only a court with no score can change, and only someone who was not on court in round {fixable.no} can take the place.</p>
          </>
        ) : state.draft ? (
          <>
            <p className="ad-note">Tap a name to swap that player.</p>
            <SwapCourts t={t} state={state} roundNo={nextNo} when="preview" swap={openSwap} setSwap={pickSwap} courts={lineups.next.courts} />
            <Resting state={state} lineup={state.draft} />
            <p className="ad-note">A swap here is lost if anyone changes status before the round starts.</p>
          </>
        ) : (
          <p className="ad-note">{allDone ? "No more rounds: the games are done." : "Nothing drawn yet."}</p>
        )}
      </section>

      <WindDownCard t={t} state={state} />

      <Fold id="rounds.settings" title="Settings for the day" summary={settings}>
        <button type="button" className="btn quiet ad-wide" onClick={regenerate}>
          Regenerate the preview
        </button>
        <h3>Courts in use</h3>
        <div className="ad-grid3" role="group" aria-label="Courts in use">
          {COURT_CHOICES.map((n) => (
            <button key={n} type="button" className={"btn ad-pickone" + (courts === n ? " on" : "")} aria-pressed={courts === n}
              aria-label={`${n} courts`} onClick={() => courts !== n && t.send({ type: "config.updated", patch: { courts: n } })}>
              {n}
            </button>
          ))}
        </div>
        <p className="ad-note">
          The next draw uses courts 1 to {courts}.{playing ? " The round being played carries on as it is." : ""}
        </p>
        <Toggle label="Start rounds by themselves" on={state.config.autoAdvance} onChange={setAuto} />
        {askAuto && autoStartsNow && (
          <div className="ad-confirm" ref={autoBox}>
            <p>Round {nextNo} starts now, and the rounds after it start by themselves. Go?</p>
            <div className="ad-row">
              <button type="button" className="btn warn"
                onClick={() => { setAskAuto(false); mine(); t.send({ type: "config.updated", patch: { autoAdvance: true } }); }}>
                Go
              </button>
              <button type="button" className="btn quiet" onClick={() => setAskAuto(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}
        <label className="ad-field">
          <span>Last whistle {lastWhistle !== null ? `(now ${whistleWhen(lastWhistle, now)})` : "(not set)"}</span>
          <div className="ad-row">
            <input type="time" value={whistle} onChange={(e) => setWhistleEdit(e.target.value)} />
            <button type="button" className="btn" disabled={whistle === whistleLive} onClick={setLastWhistle}>
              {whistle ? "Set" : "Clear"}
            </button>
          </div>
        </label>
        <p className="ad-note">
          {state.config.autoAdvance
            ? "No round starts by itself if its game would end after the last whistle."
            : "Off: the next round starts when you tap Start. Past the last whistle, Start asks first."}
        </p>
      </Fold>
    </div>
  );
}
